/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    this.cartRefreshHandler = this.cartRefreshHandler.bind(this);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('change', (evt) => {
      if (evt.target.matches('#easyship-insurance-checkbox')) {
        this.handleEasyshipToggle(evt.target);
      }
    });
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.runCartCustomLogic();
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.runCartCustomLogic();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  open(opener, elementToFocus, callback) {
    document.body.classList.add('c2-cart-drawer-open');
    super.open(opener, elementToFocus, callback);
  }

  close(callback) {
    document.body.classList.remove('c2-cart-drawer-open');
    super.close(callback);
  }

  handleClick(evt) {
    const viewAllButton = evt.target.closest('[data-c2-view-all-offers]');
    if (viewAllButton && this.contains(viewAllButton)) {
      evt.preventDefault();
      this.toggleOffers(viewAllButton);
      return;
    }

    const discountButton = evt.target.closest('[data-c2-apply-discount]');
    if (discountButton && this.contains(discountButton)) {
      evt.preventDefault();
      this.applyConfiguredDiscount(discountButton.dataset.c2ApplyDiscount, discountButton);
      return;
    }

    const offerCard = evt.target.closest('[data-c2-offer]');
    if (offerCard && this.contains(offerCard)) {
      const offerButton = offerCard.querySelector('[data-c2-apply-discount]');
      if (offerButton) {
        evt.preventDefault();
        this.applyConfiguredDiscount(offerButton.dataset.c2ApplyDiscount, offerButton);
        return;
      }
    }

    const recommendationButton = evt.target.closest('[data-c2-add-variant]');
    if (recommendationButton && this.contains(recommendationButton)) {
      evt.preventDefault();
      this.addRecommendationToCart(recommendationButton.dataset.c2AddVariant, recommendationButton);
      return;
    }

    super.handleClick(evt);
  }

  toggleOffers(button) {
    const offers = button.closest('[data-c2-offers-list]');
    if (!offers) return;

    const expanded = offers.classList.toggle('is-expanded');
    button.textContent = expanded ? offers.dataset.viewLessLabel : offers.dataset.viewAllLabel;
  }

  getAppliedDiscountCodes() {
    const source = this.querySelector('[data-applied-discounts]') || this;
    return (source.dataset.appliedDiscounts || '')
      .split('|')
      .map((code) => code.trim())
      .filter(Boolean);
  }

  dispatchCartChanged() {
    document.dispatchEvent(new CustomEvent('on:cart:change', {
      bubbles: true,
      cancelable: false
    }));
  }

  async applyConfiguredDiscount(code, button) {
    const discountCode = (code || '').trim();
    if (!discountCode || button.disabled) return;

    const existingDiscounts = this.getAppliedDiscountCodes();
    if (existingDiscounts.includes(discountCode.toUpperCase())) return;

    button.disabled = true;
    button.dataset.previousText = button.textContent;
    button.textContent = 'Applying';

    try {
      const response = await fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCode].join(',')
        })
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      this.dispatchCartChanged();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      button.disabled = false;
      button.textContent = button.dataset.previousText || 'Apply';
    }
  }

  async addRecommendationToCart(variantId, button) {
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }]
        })
      });

      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Could not add product');
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      this.dispatchCartChanged();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      button.disabled = false;
    } finally {
      button.classList.remove('is-loading');
    }
  }

  runCartCustomLogic() {
    window.setTimeout(async () => {
      if (this.querySelector('#freedelivery_minspend') && typeof window.calculateCountryFreeDelivery === 'function') {
        window.calculateCountryFreeDelivery();
      }

      await this.runEasyshipLogic();
      await this.syncGiftMarkers();
    }, 0);
  }

  runEasyshipLogic() {
    if (
      this.easyshipSyncInProgress
      || !this.querySelector('#easyship-insurance-widget')
      || !window.esCartMgr
      || typeof window.esCartMgr.handleCartChange !== 'function'
    ) {
      return Promise.resolve();
    }

    this.easyshipSyncInProgress = true;

    return Promise.resolve(window.esCartMgr.handleCartChange())
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      })
      .finally(() => {
        this.easyshipSyncInProgress = false;
      });
  }

  async handleEasyshipToggle(checkbox) {
    if (checkbox.checked || this.easyshipToggleInProgress) return;

    this.easyshipToggleInProgress = true;

    try {
      const cart = await this.getCartJson();
      const insuranceItemKeys = cart.items
        .filter((item) => (
          (item.sku && item.sku.slice(0, 5) === 'ESINS')
          || (item.product_title && item.product_title.toLowerCase().includes('easyship shipping protection'))
          || (item.title && item.title.toLowerCase().includes('easyship shipping protection'))
        ))
        .map((item) => item.key);

      if (insuranceItemKeys.length) {
        await this.removeCartItems(insuranceItemKeys);
        this.dispatchCartChanged();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      this.easyshipToggleInProgress = false;
    }
  }

  async getCartJson() {
    const response = await fetch(`${theme.routes.cart}.js`, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return response.json();
  }

  async addCartItem(variantId, quantity, properties) {
    const response = await fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity,
          properties
        }]
      })
    });

    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Could not add product');
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  }

  async changeCartItem(id, quantity, properties) {
    const body = { id, quantity };
    if (properties) body.properties = properties;

    const response = await fetch(`${theme.routes.cart}/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });

    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Could not update product');
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  }

  async removeCartItems(ids) {
    const updates = {};
    ids.forEach((id) => {
      if (id) updates[id] = 0;
    });

    if (!Object.keys(updates).length) return;

    const response = await fetch(theme.routes.cartUpdate, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Could not remove product');
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  }

  async syncGiftMarkers() {
    if (this.cartGiftSyncInProgress) return;

    const genericGiftAdd = this.querySelector('#jsc-add-gwp');
    const genericGiftUpdate = this.querySelector('#jsc-update-gwp');
    const genericGiftRemovals = [...this.querySelectorAll('.jsc-remove-gwp')];
    const gwpAdds = [...this.querySelectorAll('.jsc-hidden-add-gwp')];
    const gwpRemovals = [...this.querySelectorAll('.jsc-hidden-remove-gwp')];
    const hasGiftWork = genericGiftAdd || genericGiftUpdate || genericGiftRemovals.length || gwpAdds.length || gwpRemovals.length;

    if (!hasGiftWork) return;

    this.cartGiftSyncInProgress = true;

    try {
      const cart = await this.getCartJson();

      if (genericGiftAdd) {
        await this.addCartItem(genericGiftAdd.value, 1, { Campaign: 'Gift' });
      }

      if (genericGiftUpdate) {
        await this.changeCartItem(genericGiftUpdate.value, 1);
      }

      if (genericGiftRemovals.length) {
        await this.removeCartItems(genericGiftRemovals.map((el) => el.value));
      }

      for (const marker of gwpAdds) {
        const promo = marker.dataset.promo;
        const quantity = Math.max(parseInt(marker.dataset.qty, 10) || 1, 1);
        const properties = {
          _GWP_Promo: promo,
          _GWP: 'true'
        };
        const existingItem = cart.items.find((item) => item.properties && item.properties._GWP_Promo === promo);

        if (existingItem) {
          await this.changeCartItem(existingItem.key, quantity, properties);
        } else {
          await this.addCartItem(marker.value, quantity, properties);
        }
      }

      if (gwpRemovals.length) {
        await this.removeCartItems(gwpRemovals.map((marker) => {
          const existingItem = cart.items.find((item) => (
            item.key === marker.value
            || (String(item.variant_id) === marker.value && item.properties && item.properties._GWP)
          ));

          return existingItem ? existingItem.key : marker.value;
        }));
      }

      this.dispatchCartChanged();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      this.cartGiftSyncInProgress = false;
    }
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }
}

window.customElements.define('cart-drawer', CartDrawer);
